import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Modal,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import ProfessionalDetails from "./ProfessionalDetails";
import "./../../new-booking-form/components/style.scss";
import PersonalDetailStepIcon from "../../../assets/component-icon/PersonalDetailStepIcon";
import ContactDetailStepIcon from "../../../assets/component-icon/ContactDetailStepIcon";
import ProfessionalDetailStepIcon from "../../../assets/component-icon/ProfessionalDetailStepIcon";
import SnackbarAlert from "../../../shared-components/snackbar-alert/SnackbarAlert";
import AdditionalInfo from "./AdditionalInfo";
import PersonalDetailsIndividual from "./PersonalDetailsIndividual";
import {
  setApplicantActiveStep,
  setHasContinuedAsMinor,
  setHasContinuedAsMinorModal,
} from "../new-booking-form.slice";
import { getTotalYears } from "../../../utilis/common-funtions";
import StepperIconRoot from "../../../shared-components/stepper/StepperIconRoot";
import { MinorAge } from "../../../config/constant";

const ApplicantDetailIndividual = ({ applicantNo }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const personalDetailsRef = useRef();
  const contactDetailsRef = useRef();
  const professionalDetailsRef = useRef();
  const { applicantActiveStep, applicantData, hasContinuedAsMinor } =
    useSelector((state) => state.newBookingForm);
  const [applicantDetails, setApplicantDetails] = useState(null);
  const applicantKey = `applicant${applicantNo}`;
  const currentApplicant = applicantData?.[applicantKey];
  const personalDetails = currentApplicant?.personalDetails;
  // Check if the current applicant is a minor
  useEffect(() => {
    if (personalDetails?.dob) {
      const age = getTotalYears(personalDetails.dob);
      if (
        Number.parseInt(age) < MinorAge
      ) {
        dispatch(setHasContinuedAsMinor(true));
      } else {
        dispatch(setHasContinuedAsMinor(false));
      }
    } else {
      dispatch(setHasContinuedAsMinor(false));
    }
      if (personalDetails?.dob) {
      const age = getTotalYears(personalDetails.dob);
      if (
        Number.parseInt(age) < MinorAge &&
        !applicantData?.[applicantKey]?.hasContinuedAsMinor
      ) {
        dispatch(setHasContinuedAsMinorModal(true));
      } else {
        dispatch(setHasContinuedAsMinorModal(false));
      }
    } else {
      dispatch(setHasContinuedAsMinorModal(false));
    }

    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalDetails?.dob]);

  const stepsApplicantDetails = hasContinuedAsMinor
    ? [
        t("applicantDetailIndvidual.steps.personalDetails"),
        t("applicantDetailIndvidual.steps.additionalInformation"),
      ]
    : [
        t("applicantDetailIndvidual.steps.personalDetails"),
        t("applicantDetailIndvidual.steps.additionalInformation"),
        t("applicantDetailIndvidual.steps.professionalDetails"),
      ];

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [nextStep, setNextStep] = useState(null);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const stepperRef = useRef(null);

  useEffect(() => {
    if (isMobile && stepperRef.current) {
      if (applicantActiveStep === stepsApplicantDetails.length - 1) {
        stepperRef.current.scrollTo({
          left: stepperRef.current.scrollWidth,
          behavior: "smooth",
        });
      } else {
        stepperRef.current.scrollTo({
          left: 0,
          behavior: "smooth",
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantActiveStep, isMobile]);

  useEffect(() => {
    if (applicantData) {
      setApplicantDetails(applicantData);
    }
  }, [applicantData]);

  const [snackbarConfig, setSnackbarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });

  function ColorlibStepIcon(props) {
    const { active, className, icon } = props;

    // Determine which steps are completed for the current applicant
    const isCompleted1 =
      icon === 1 &&
      applicantDetails?.[`applicant${props.applicantNo}`]?.personalDetails &&
      applicantActiveStep !== 0;

    const isCompleted2 =
      icon === 2 &&
      applicantDetails?.[`applicant${props.applicantNo}`]?.contactDetails &&
      applicantActiveStep !== 1;

    const isCompleted3 =
      icon === 3 &&
      applicantDetails?.[`applicant${props.applicantNo}`]
        ?.professionalDetails &&
      applicantActiveStep !== 2;

    const icons = {
      1: <PersonalDetailStepIcon show={active} completed={isCompleted1} />,
      2: <ContactDetailStepIcon show={active} completed={isCompleted2} />,
      3: <ProfessionalDetailStepIcon show={active} completed={isCompleted3} />,
    };

    return (
      <StepperIconRoot
        ownerState={{
          active,
          isCompleted1,
          isCompleted2,
          isCompleted3,
          variant: "colorlib", // use "colorlib" for this style
        }}
        className={className}
      >
        {icons[icon]}
      </StepperIconRoot>
    );
  }

  const handleStep = (step) => async () => {
    const currentApplicant = applicantDetails?.[`applicant${applicantNo}`];

    const hasPersonalDetails = currentApplicant?.personalDetails;
    const hasContactDetails = currentApplicant?.contactDetails;
    const hasProfessionalDetails = currentApplicant?.professionalDetails;

    if (step === 1 && !hasPersonalDetails) return;
    if (step === 1 && !hasContactDetails) return;
    if (step === 2 && !(hasPersonalDetails && hasContactDetails)) return;

    if (
      (step === 0 &&
        applicantActiveStep === 1 &&
        hasPersonalDetails &&
        !hasContactDetails) ||
      (step === 1 &&
        applicantActiveStep === 2 &&
        hasContactDetails &&
        !hasProfessionalDetails) ||
      (step === 0 &&
        applicantActiveStep === 2 &&
        hasPersonalDetails &&
        !hasProfessionalDetails)
    ) {
      setShowConfirmDialog(true);
      setNextStep(step);
      return;
    }

    if (hasPersonalDetails && hasContactDetails && hasProfessionalDetails) {
      if (
        (professionalDetailsRef?.current &&
          Object.keys(professionalDetailsRef?.current?.errors).length > 0) ||
        (contactDetailsRef?.current &&
          Object.keys(contactDetailsRef?.current?.errors).length > 0) ||
        (personalDetailsRef?.current &&
          Object.keys(personalDetailsRef?.current?.errors).length > 0)
      ) {
        setSnackbarConfig({
          open: true,
          message: t("applicantDetailIndvidual.snackbar.mandatoryFields"),
          severity: "error",
        });
        return;
      }
      if (applicantActiveStep === 2 && step !== 2) {
        await professionalDetailsRef?.current?.saveUpdatedData();
      }
      if (applicantActiveStep === 1 && step !== 1) {
        await contactDetailsRef?.current?.handleSaveForm();
      }
      if (applicantActiveStep === 0 && step !== 0) {
        await personalDetailsRef?.current?.handleSubmit();
      }
    }

    if (applicantActiveStep !== step) {
      dispatch(setApplicantActiveStep(step));
    }
  };

  const handleDialogClose = (confirm) => {
    setShowConfirmDialog(false);
    if (confirm && nextStep !== null) {
      dispatch(setApplicantActiveStep(nextStep));
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarConfig({ open: false, message: "", severity: "" });
  };

  useEffect(()=>{
    if(applicantActiveStep === 2 && currentApplicant?.hasContinuedAsMinor){
      dispatch(setApplicantActiveStep(1))
    }
    if(!currentApplicant){
      dispatch(setApplicantActiveStep(0))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[applicantActiveStep,applicantData])
  
  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Stepper
          activeStep={applicantActiveStep}
          sx={{ mb: 3, whiteSpace: "nowrap",}}
          ref={stepperRef}
          className="steeperPersonal"
        >
          {stepsApplicantDetails.map((label, index) => (
            <Step
              key={label}
              onClick={handleStep(index)}
              sx={{
                "& .MuiStepLabel-root": { cursor: "pointer" },
                "& .MuiStepLabel-label.Mui-active": {
                  color: "#1A407D",
                  fontSize: "14px",
                  fontWeight: "600",
                  lineHeight: "22px",
                },
              }}
            >
              <StepLabel
                StepIconComponent={(props) => (
                  <ColorlibStepIcon {...props} applicantNo={applicantNo} />
                )}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Modal
        open={showConfirmDialog}
        onClose={() => handleDialogClose(false)}
        className="saveLaterModal"
        hideBackdrop={true}
        disableEscapeKeyDown={true}
      >
        <div className="modalContainer">
          <Box sx={{ border: "0" }}>
            <Typography className="modalTitle" variant="h6" component="h2">
              {t("applicantDetailIndvidual.modal.unsavedChanges")}
            </Typography>
            <Typography className="modalTitle" variant="h6" component="h2">
              {t("applicantDetailIndvidual.modal.or")}
            </Typography>
            <Typography className="modalTitle" variant="h6" component="h2">
              {t("applicantDetailIndvidual.modal.saveAndProceed")}
            </Typography>
          </Box>
          <div className="btnSignLateModal titleDear marginTop">
            <button onClick={() => handleDialogClose(true)}>
              {t("applicantDetailIndvidual.modal.yes")}
            </button>
            <button onClick={() => handleDialogClose(false)}>
              {t("applicantDetailIndvidual.modal.no")}
            </button>
          </div>
        </div>
      </Modal>

      {(applicantActiveStep === 0 || applicantActiveStep === -1) && (
        <PersonalDetailsIndividual
          applicantNo={applicantNo}
          ref={personalDetailsRef}
        />
      )}
      {applicantActiveStep === 1 && (
        <AdditionalInfo applicantNo={applicantNo} ref={contactDetailsRef} />
      )}
      {applicantActiveStep === 2 && !hasContinuedAsMinor && (
        <ProfessionalDetails
          applicantNo={applicantNo}
          ref={professionalDetailsRef}
        />
      )}
      <SnackbarAlert
        open={snackbarConfig.open}
        message={snackbarConfig.message}
        severity={snackbarConfig.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
};

export default ApplicantDetailIndividual;
